import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function TipsPage() {
  const [tipFormData, setTipFormData] = useState({
    tipType: '',
    urgency: '',
    location: '',
    dateTime: '',
    description: '',
    suspectDescription: '',
    vehicleDescription: '',
    anonymous: false,
    contactInfo: '',
    followUp: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tipFormData.tipType || !tipFormData.urgency || !tipFormData.location || !tipFormData.description) {
      setSubmitStatus('error');
      return;
    }

    if (tipFormData.description.length > 500 || tipFormData.suspectDescription.length > 500 || tipFormData.vehicleDescription.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase.from('tips').insert({
        tip_type: tipFormData.tipType,
        urgency: tipFormData.urgency,
        location: tipFormData.location,
        date_time: tipFormData.dateTime ? new Date(tipFormData.dateTime).toISOString() : null,
        description: tipFormData.description,
        suspect_description: tipFormData.suspectDescription || null,
        vehicle_description: tipFormData.vehicleDescription || null,
        anonymous: tipFormData.anonymous,
        contact_info: !tipFormData.anonymous && tipFormData.contactInfo ? tipFormData.contactInfo : null,
        follow_up: !tipFormData.anonymous ? tipFormData.followUp : false,
        status: 'Pending Review',
        assigned_to: 'Unassigned',
        assigned_department: null
      });

      if (error) throw error;

      setSubmitStatus('success');
      setTipFormData({
        tipType: '',
        urgency: '',
        location: '',
        dateTime: '',
        description: '',
        suspectDescription: '',
        vehicleDescription: '',
        anonymous: false,
        contactInfo: '',
        followUp: false
      });
    } catch (error) {
      console.error('Tip submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Arial Black, sans-serif' }}>RMRP Federal Investigations & Intelligence Portal</h1>
                <p className="text-sm text-slate-300"></p>
              </div>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/tips" className="text-blue-400 cursor-pointer">Submit Tips</Link>
              <Link to="/login" className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Investigator Login</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Submit a Tip</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Help our investigators solve cases by providing detailed information about suspicious activities or crimes
          </p>
        </div>
      </section>

      {/* Tip Form */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-8" id="detailed-tip-form" data-readdy-form>
                {/* Basic Information */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-information-line mr-2"></i>
                    Basic Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Type of Incident</label>
                      <select 
                        name="tipType"
                        value={tipFormData.tipType}
                        onChange={(e) => setTipFormData({...tipFormData, tipType: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Select incident type</option>
                        <option value="suspicious-activity">Suspicious Activity</option>
                        <option value="drug-trafficking">Drug Trafficking</option>
                        <option value="theft-robbery">Theft/Robbery</option>
                        <option value="assault-violence">Assault/Violence</option>
                        <option value="fraud-scam">Fraud/Scam</option>
                        <option value="weapons-violation">Weapons Violation</option>
                        <option value="gang-activity">Gang Activity</option>
                        <option value="corruption">Corruption</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Urgency Level</label>
                      <select 
                        name="urgency"
                        value={tipFormData.urgency}
                        onChange={(e) => setTipFormData({...tipFormData, urgency: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Select urgency</option>
                        <option value="low">Low - General Information</option>
                        <option value="medium">Medium - Needs Investigation</option>
                        <option value="high">High - Immediate Attention</option>
                        <option value="critical">Critical - Emergency Response</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Location and Time */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-map-pin-line mr-2"></i>
                    Location & Time
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={tipFormData.location}
                        onChange={(e) => setTipFormData({...tipFormData, location: e.target.value})}
                        placeholder="Street address, intersection, or landmark"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Date & Time</label>
                      <input
                        type="datetime-local"
                        name="dateTime"
                        value={tipFormData.dateTime}
                        onChange={(e) => setTipFormData({...tipFormData, dateTime: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                {/* Detailed Description */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-file-text-line mr-2"></i>
                    Incident Details
                  </h3>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Detailed Description</label>
                    <textarea
                      name="description"
                      value={tipFormData.description}
                      onChange={(e) => setTipFormData({...tipFormData, description: e.target.value})}
                      placeholder="Provide a detailed account of what you observed, including sequence of events, people involved, and any other relevant information..."
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      required
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">{tipFormData.description.length}/500 characters</p>
                  </div>
                </div>

                {/* Suspect Information */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    Suspect Information (If Applicable)
                  </h3>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Suspect Description</label>
                    <textarea
                      name="suspectDescription"
                      value={tipFormData.suspectDescription}
                      onChange={(e) => setTipFormData({...tipFormData, suspectDescription: e.target.value})}
                      placeholder="Physical description, clothing, behavior, known associates, etc."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">{tipFormData.suspectDescription.length}/500 characters</p>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-car-line mr-2"></i>
                    Vehicle Information (If Applicable)
                  </h3>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle Description</label>
                    <textarea
                      name="vehicleDescription"
                      value={tipFormData.vehicleDescription}
                      onChange={(e) => setTipFormData({...tipFormData, vehicleDescription: e.target.value})}
                      placeholder="Make, model, color, license plate, distinctive features, etc."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">{tipFormData.vehicleDescription.length}/500 characters</p>
                  </div>
                </div>

                {/* Contact Preferences */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <i className="ri-contacts-line mr-2"></i>
                    Contact Preferences
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        name="anonymous"
                        id="anonymous"
                        checked={tipFormData.anonymous}
                        onChange={(e) => setTipFormData({...tipFormData, anonymous: e.target.checked})}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="anonymous" className="text-sm text-slate-700">Submit this tip anonymously</label>
                    </div>
                    
                    {!tipFormData.anonymous && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Contact Information</label>
                          <input
                            type="text"
                            name="contactInfo"
                            value={tipFormData.contactInfo}
                            onChange={(e) => setTipFormData({...tipFormData, contactInfo: e.target.value})}
                            placeholder="Phone number, Discord username, or in-game character name"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            disabled={isSubmitting}
                          />
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            name="followUp"
                            id="followUp"
                            checked={tipFormData.followUp}
                            onChange={(e) => setTipFormData({...tipFormData, followUp: e.target.checked})}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            disabled={isSubmitting}
                          />
                          <label htmlFor="followUp" className="text-sm text-slate-700">I'm willing to be contacted for follow-up questions</label>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2"></i>
                      <p className="text-green-800 font-medium">Detailed tip submitted successfully! Our investigators will review your information.</p>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 font-medium">Error submitting tip. Please check all required fields and character limits, then try again.</p>
                    </div>
                  </div>
                )}

                <div className="pt-6 border-t border-slate-200">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting Tip...
                      </span>
                    ) : (
                      'Submit Tip to Investigators'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Information Cards */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-green-800 mb-2">Your Safety First</h4>
                    <p className="text-sm text-green-700">
                      Never put yourself in danger to gather information. If you witness a crime in progress, 
                      contact emergency services immediately.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-lock-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Confidentiality</h4>
                    <p className="text-sm text-blue-700">
                      All tips are treated with strict confidentiality. Anonymous tips are processed 
                      without any identifying information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
